/** The part of a slot this module needs — shared by `ISlot` and the agenda's `ReducedSlot`. */
interface AttendeeSlot {
  user: { id: string };
}

/**
 * One slot per distinct attendee, in the order the slots come back.
 *
 * A reservation stores one slot per resource *per attendee*, so a booking of 3 rooms for
 * 2 people holds 6 slots covering 2 people. Anything showing people — avatars, attendee
 * lists, counts — wants this rather than the raw slots, or the same person is rendered
 * once per room.
 *
 * Generic over the slot type so callers keep whichever slot shape they passed in.
 */
export function getScheduleAttendeeSlots<TSlot extends AttendeeSlot>(
  schedule: { slots?: TSlot[] | null } | null | undefined
): TSlot[] {
  const attendeeSlots: TSlot[] = [];
  const seenUserIds = new Set<string>();

  for (const slot of schedule?.slots ?? []) {
    const userId = slot?.user?.id;
    if (!userId || seenUserIds.has(userId)) {
      continue;
    }
    seenUserIds.add(userId);
    attendeeSlots.push(slot);
  }

  return attendeeSlots;
}

/** The distinct attendees of a reservation, in the order their slots come back. */
export function getScheduleAttendeeUsers<TSlot extends AttendeeSlot>(
  schedule: { slots?: TSlot[] | null } | null | undefined
): TSlot["user"][] {
  return getScheduleAttendeeSlots(schedule).map((slot) => slot.user);
}
